import { AppEnv } from './env.model';
import { Env } from './env';

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

/**
 * Surcharge runtime de l'environnement compilé.
 *
 * Le déploiement peut poser un `config.json` à côté de l'app (même shape
 * qu'`AppEnv`, partiel accepté) : `main.ts` le fetch AVANT le bootstrap et
 * l'applique ici. Un seul build est ainsi promu local → dev → prod ; changer
 * une URL = éditer le JSON sur le serveur, sans rebuild.
 *
 * Sans fichier (dev-server local, ou 404 → fallback SPA en text/html rejeté
 * par main.ts), les valeurs compilées (`env.ts` / fileReplacements) restent.
 *
 * Validation clé par clé : une valeur absente ou du mauvais type est ignorée
 * (on garde la valeur compilée) — un config.json partiel ou cassé ne peut pas
 * produire un Env incohérent.
 */
export function applyRuntimeEnv(raw: unknown): void {
  if (typeof raw !== 'object' || raw === null) {
    return;
  }

  const cfg = raw as Partial<AppEnv>;
  const env = Env as Mutable<AppEnv>;
  let applied = false;

  if (typeof cfg.apiBaseUrl === 'string') {
    env.apiBaseUrl = cfg.apiBaseUrl;
    applied = true;
  }
  if (typeof cfg.storageBaseUrl === 'string') {
    env.storageBaseUrl = cfg.storageBaseUrl;
    applied = true;
  }

  const reverb = cfg.reverb;
  if (typeof reverb === 'object' && reverb !== null) {
    env.reverb = {
      appKey: typeof reverb.appKey === 'string' ? reverb.appKey : Env.reverb.appKey,
      host: typeof reverb.host === 'string' ? reverb.host : Env.reverb.host,
      port: typeof reverb.port === 'number' ? reverb.port : Env.reverb.port,
      useTLS: typeof reverb.useTLS === 'boolean' ? reverb.useTLS : Env.reverb.useTLS,
    };
    applied = true;
  }

  // Diagnostic ops : rend visible quelle config l'app utilise réellement.
  // Silencieux si le fichier n'a rien surchargé (placeholder `{}` embarqué
  // dans public/ pour éviter un 404 au démarrage local).
  if (applied) {
    console.info(
      `[env] config runtime appliquée — api=${env.apiBaseUrl} ws=${env.reverb.host}:${env.reverb.port} tls=${env.reverb.useTLS}`,
    );
  }
}
